import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { HERO_VIDEO, HERO_VIDEO_LIGHT, HERO_POSTER_LIGHT, HERO_POSTER_DARK } from "../lib/media";
import { DispatchCounter, MobileDispatchStrip } from "./DispatchCounter";

const forceMuted = (el) => {
  if (!el) return;
  el.muted = true;
  el.defaultMuted = true;
  el.play().catch(() => {});
};

function HeroMedia({ isDark }) {
  const [playing, setPlaying] = useState(false);
  const src = isDark ? HERO_VIDEO : HERO_VIDEO_LIGHT;
  const poster = isDark ? HERO_POSTER_DARK : HERO_POSTER_LIGHT;
  return (
    <div key={src} className={`absolute inset-0 overflow-hidden pointer-events-none ${isDark ? "opacity-30" : ""}`}>
      <img data-testid="hero-kenburns" src={poster} alt="" aria-hidden="true" className={`absolute inset-0 w-full h-full object-cover ${isDark ? "" : "hero-kenburns"}`} />
      <video ref={forceMuted} data-testid="hero-video" src={src} autoPlay muted loop playsInline onPlaying={() => setPlaying(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${playing ? "opacity-100" : "opacity-0"}`} />
    </div>
  );
}

export function HeroSection({ isDark = true }) {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen min-h-[100svh] flex items-center justify-center circuit-bg overflow-hidden"
    >
      <HeroMedia isDark={isDark} />
      <DispatchCounter />
      <div className="hero-overlay absolute inset-0 bg-gradient-to-b from-ib-bg/70 via-ib-bg/40 to-ib-bg pointer-events-none" />

      {/* Gradient orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-ib-blue/10 rounded-full blur-[128px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-ib-lime/5 rounded-full blur-[128px] pointer-events-none" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center pt-24">
        {/* Badge */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="hero-glass inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-ib-steel/40 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-ib-lime animate-pulse" />
          <span className="font-jetbrains text-xs text-ib-text-secondary tracking-wide">
            From chaos to control — Steel meets software
          </span>
        </motion.div>

        {/* Headline */}
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="font-outfit font-extrabold text-4xl sm:text-5xl lg:text-7xl tracking-tight leading-[1.05] mb-6"
        >
          <span className="text-ib-text">The Digital Backbone of </span>
          <br className="hidden sm:block" />
          <span className="text-ib-blue glow-blue-text">Metal Trading</span>{" "}
          <span className="text-ib-text">in India</span>
        </motion.h1>

        {/* Sub-headline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="hero-sub font-manrope text-base md:text-lg text-ib-text-secondary max-w-2xl mx-auto mb-10 leading-relaxed"
        >
          One platform for metal trading — real-time rates, verified suppliers,
          and deals closed end-to-end. IronByte replaces scattered calls and
          paperwork with a single digital system.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="hero-sub-light font-manrope text-base md:text-lg font-semibold text-ib-text max-w-2xl mx-auto mb-10"
          data-testid="hero-sub-light"
        >
          Source, price and deliver steel across India — verified, tracked and on time.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            data-testid="hero-explore-marketplace-btn"
            onClick={() => scrollTo("#marketplace")}
            className="bg-ib-blue hover:bg-ib-blue/90 text-white font-outfit text-sm px-8 py-6 rounded-full gap-2 transition-all duration-200 hover:shadow-[0_0_30px_rgba(0,123,255,0.3)]"
          >
            Explore Marketplace
            <ArrowRight className="w-4 h-4" />
          </Button>
          <Button
            data-testid="hero-get-quote-btn"
            onClick={() => scrollTo("#quote")}
            variant="outline"
            className="hero-glass border-ib-steel/50 text-ib-text hover:text-ib-lime hover:border-ib-lime/40 font-outfit text-sm px-8 py-6 rounded-full gap-2 transition-all duration-200"
          >
            <FileText className="w-4 h-4" />
            Get Custom Quote
          </Button>
        </motion.div>

        {/* Trust bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="hero-glass mt-10 sm:mt-14 grid grid-cols-2 sm:inline-flex sm:flex-wrap items-center justify-center gap-x-4 sm:gap-x-6 gap-y-2 px-4 sm:px-6 py-3 text-center rounded-2xl border border-ib-steel/20 text-ib-text font-jetbrains text-xs font-semibold"
        >
          <span>6,588 MT Delivered</span>
          <span className="hidden sm:block w-1 h-1 bg-ib-steel rounded-full" />
          <span>₹100 Cr+ GMV Run-Rate</span>
          <span className="hidden sm:block w-1 h-1 bg-ib-steel rounded-full" />
          <span>95% Repeat Rate</span>
          <span className="hidden sm:block w-1 h-1 bg-ib-steel rounded-full" />
          <span>Certificate-Backed Quality</span>
        </motion.div>
        <MobileDispatchStrip />
      </div>
    </section>
  );
}
